import React, { CSSProperties, useMemo } from 'react'
import styles from '../../styles'
import { makeStyles } from 'tss-react/mui'
import { useIntl } from 'react-intl'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

const useStyles = makeStyles()((theme) => (
	{
		...styles(theme),
		linkStyle: {
			display: 'flex',
			alignItems: 'center',
			textDecoration: 'none'
		},
		fitContent: {
			width: 'fit-content'
		},
		noAnimationStyle: {
			'&:before': {
				content: 'none'
			}
		}
	}
))

export interface Alias {
	fr: string
	en: string
}

export interface ModelLink {
	color?: string,
	textStyle?: string,
	children: React.ReactNode,
	onClick?: () => void,
	ariaLabel?: string,
	hasChevron?: boolean,
	hasChevronLeft?: boolean,
	className?: string,
	href?: string,
	prevent?: boolean,
	state?: any,
	noAnimation?: boolean
	fullWidth?: boolean,
	flex?: boolean
	alias?: Alias,
	target?: string,
	style?: CSSProperties,
	typeBouton?: boolean
}

type LinkProps = ModelLink

const Link: React.FC<LinkProps> = (
	{
		color = 'colorBlue',
		textStyle = 'text8',
		children,
		onClick,
		ariaLabel,
		hasChevron = true,
		hasChevronLeft = false,
		className = '',
		href = '/',
		prevent = false,
		state = {},
		noAnimation = false,
		fullWidth = false,
		flex = true,
		alias,
		target,
		style,
		typeBouton = false
	}
) => {
	const { classes, cx } = useStyles()
	const intl = useIntl()

	const linkClass = cx(
		classes[textStyle as keyof typeof styles],
		classes[color as keyof typeof styles],
		classes.cursorPointer,
		{
			[classes.noAnimationStyle]: noAnimation,
			[classes.fitContent]: !fullWidth,
			[classes.linkStyle]: flex,
			'lienStyleBouton': typeBouton,
			[classes.backgroundAccessibility]: typeBouton
		},
		className
	)

	const linkOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		if (prevent) {
			event.preventDefault()
		}
		if (onClick) {
			onClick()
		}
	}

	/**
	 * Permet de construire les paramètres issus du state (anciennement react-router et maintenant Next.js)
	 */
	const params = useMemo<string>(() => {
		if (state) {
			const keys = Object.keys(state)
			if (keys.length > 0) {
				return `?${keys.map((key) => {
					return `${key}=${encodeURIComponent(state[key])}`
				}).join('&')}`
			}
		}
		return ''
	}, [state])

	const getHrefByLocale = (href: string, alias?: Alias) => {
		// Dans le cas d'une gestion via alias renvoyé du back
		if (!!alias && alias.fr && alias.en) {
			return alias[intl.locale as keyof Alias]
		}

		// Sinon on renvoie la route
		return href
	}

	return (
		<>
			{
				href?.startsWith('/') ?
					<a
						href={`${getHrefByLocale(href, alias)}${params}`}
						color="initial"
						aria-label={ariaLabel}
						className={linkClass}
						target={target ?? '_self'}
						style={style}
						onClick={(e) => linkOnClick(e)}
					>
						{
							hasChevronLeft &&
							<ChevronLeftIcon />
						}
						{children}
						{
							hasChevron &&
							<ChevronRightIcon />
						}
					</a>
					:
					<a
						href={href}
						className={linkClass}
						target={target ?? '_blank'}
						style={style}
						rel="noopener noreferrer"
					>
						{
							hasChevronLeft &&
							<ChevronLeftIcon />
						}

						{children}

						{
							hasChevron &&
							<ChevronRightIcon />
						}
					</a>
			}
		</>
	)
}

export default Link
